import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { ArrowLeft, Eye, FileText, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../../types/project';
import type { SpecificationDocument } from '../../../../types/index';
import {
  defaultSpoolDisplayName,
  getNumberOfSpoolsFromPipe,
  getSpoolDetailsFromPipe,
  normalizeSpoolDetailsRows,
} from '../../../../types/setup';
import { SPEC_TYPE_LABELS } from '../../../../constants/specificationConstants';
import { cn } from '../../../../../../lib/utils';
import { Notification } from '../../../../../../components/ui/custom-notification';
import {
  CoatingSpoolChecklistEditor,
  type CoatingSpoolPersistPayload,
} from '../Coating/CoatingSpoolChecklistEditor';
import {
  createSeedChecklistSections,
  newCoatingChecklistId,
  type CoatingChecklistTemplateSection,
} from '../Coating/coatingChecklistMockData';
import { listFatSpecificationTemplates, saveFatSpecificationTemplate } from './fatSpecificationTemplateApi';
import { findFatSpecificationTemplateForSpool } from './fatSpecificationTemplateResolve';
import type { FatSpecificationTemplateDto } from './fatSpecificationTemplateTypes';
import { FAT_CHECKLIST_SPEC_CATEGORY } from './fatSpecificationConstants';

type EditRoute =
  | { kind: 'picker' }
  | {
      kind: 'edit';
      spoolIndex: number;
      spoolLabel: string;
      sectionId: string;
      sectionLabel: string;
    };

export interface FatChecklistSpecificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  project?: DashboardProject;
  specification: SpecificationDocument;
  canEdit?: boolean;
  canReport?: boolean;
  spoolSource?: unknown;
  showSpoolFatChecklist?: boolean;
  fatTemplateListVersion?: number;
}

function draftFromTemplate(template: FatSpecificationTemplateDto | null): Partial<CoatingSpoolPersistPayload> | null {
  const payload = template?.payload as Partial<CoatingSpoolPersistPayload> | null | undefined;
  if (!payload || typeof payload !== 'object') return null;
  return payload;
}

function FatSpoolPickerCard({
  label,
  displaySections,
  hasData,
  canEdit,
  onOpenSection,
  onAddCategory,
}: {
  label: string;
  displaySections: CoatingChecklistTemplateSection[];
  hasData: boolean;
  canEdit: boolean;
  onOpenSection: (sectionId: string, sectionLabel: string) => void;
  onAddCategory?: (category: string) => Promise<string | void> | string | void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    const firstId = displaySections[0]?.id ?? '';
    if (!selectedId || !displaySections.some((s) => s.id === selectedId)) {
      setSelectedId(firstId);
    }
  }, [displaySections, selectedId]);

  const existingCategoriesLower = React.useMemo(
    () => new Set(displaySections.map((s) => s.category.trim().toLowerCase())),
    [displaySections],
  );

  const handleAddCategory = async () => {
    if (!onAddCategory) return;
    const trimmed = newCategoryName.trim();
    if (!trimmed) return;
    if (existingCategoriesLower.has(trimmed.toLowerCase())) {
      Notification.warning('FAT checklist', `Category "${trimmed}" already exists.`);
      return;
    }
    const newId = await onAddCategory(trimmed);
    if (typeof newId === 'string' && newId) setSelectedId(newId);
    setNewCategoryName('');
    setAddOpen(false);
  };

  return (
    <Card
      className={cn(
        'flex min-h-0 flex-col border border-violet-200/80 shadow-sm transition-shadow',
        hasData ? 'bg-gradient-to-b from-violet-50/90 to-white' : 'bg-white',
      )}
    >
      <CardContent className="flex min-h-0 flex-col p-0">
        <div className="flex min-w-0 items-start justify-between gap-2 p-4 pb-0">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span
              className={cn(
                'h-2.5 w-2.5 flex-shrink-0 rounded-full ring-2 ring-white',
                hasData ? 'bg-violet-500' : 'bg-slate-300',
              )}
              title={hasData ? 'Checklist saved' : 'Not saved yet'}
              aria-hidden
            />
            <div className="min-w-0">
              <h5 className="truncate text-base font-semibold leading-tight text-slate-900" title={label}>
                {label}
              </h5>
            </div>
          </div>
        </div>

        {displaySections.length > 0 ? (
          <div className="mx-4 mt-3 rounded-lg border border-slate-200/90 bg-white" aria-label="FAT checklist categories">
            <div className="border-b border-slate-200/80 bg-slate-50/80 px-3 py-2 text-xs font-medium text-slate-700">
              Categories
            </div>
            <ul className="m-0 max-h-[min(42vh,450px)] list-none space-y-1.5 overflow-y-auto p-2.5 pl-2">
              {displaySections.map((section, idx) => (
                <li
                  key={section.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(section.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(section.id);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-400',
                    selectedId === section.id
                      ? 'border-amber-300/95 bg-amber-50/90 text-slate-900 shadow-sm'
                      : 'border-slate-200/90 bg-white text-slate-800 hover:border-slate-300',
                  )}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-[10px] font-semibold text-slate-600">
                    {idx + 1}
                  </span>
                  <span className="min-w-0 font-medium leading-snug">{section.category}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mx-4 mt-3 text-xs text-slate-500">No checklist categories.</p>
        )}

        <div className="mt-auto flex flex-col gap-2 border-t border-slate-200/80 bg-slate-50/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          {canEdit && onAddCategory ? (
            <Button
              type="button"
              size="sm"
              className="h-8 justify-start gap-1 rounded-md bg-hero-red px-3 text-left text-xs font-semibold text-white hover:bg-hero-red-60"
              onClick={() => setAddOpen(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Category
            </Button>
          ) : null}
          <div className="flex justify-end sm:ml-auto sm:shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-violet-300 bg-transparent text-violet-700 hover:bg-violet-50/60 hover:text-violet-900"
              disabled={!selectedId}
              onClick={() => {
                const section = displaySections.find((s) => s.id === selectedId);
                if (section) onOpenSection(section.id, section.category);
              }}
            >
              <Eye className="mr-1 h-4 w-4" />
              Open
            </Button>
          </div>
        </div>
      </CardContent>

      {canEdit && onAddCategory && addOpen ? (
        <div className="border-t border-violet-100 bg-violet-50/40 px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-[11px] font-medium text-slate-700">Category name</label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Visual Inspection, Hydrotest"
                className="h-9 bg-white text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddCategory();
                  }
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewCategoryName('');
                  setAddOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-hero-red text-white hover:bg-hero-red-60"
                disabled={!newCategoryName.trim()}
                onClick={() => void handleAddCategory()}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export const FatChecklistSpecificationModal: React.FC<FatChecklistSpecificationModalProps> = ({
  isOpen,
  onClose,
  specification,
  project,
  canEdit = true,
  canReport = true,
  spoolSource,
  showSpoolFatChecklist = false,
  fatTemplateListVersion = 0,
}) => {
  const [templates, setTemplates] = useState<FatSpecificationTemplateDto[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateListVersion, setTemplateListVersion] = useState(0);
  const [stack, setStack] = useState<EditRoute>({ kind: 'picker' });

  const projectId = project?.project?.projectId;
  const pipeId = specification.pipeId;
  const apiReady = projectId != null && projectId !== '' && pipeId != null;
  const uploadedAt = specification.uploadDate ? new Date(specification.uploadDate).toLocaleString() : null;

  const showChecklist = Boolean(showSpoolFatChecklist && projectId != null && pipeId != null);
  const nSpools = showChecklist ? getNumberOfSpoolsFromPipe(spoolSource as Record<string, unknown>) : 0;
  const details = showChecklist ? getSpoolDetailsFromPipe(spoolSource as any) : [];
  const norm = showChecklist ? normalizeSpoolDetailsRows(nSpools, details) : [];

  useEffect(() => {
    if (!isOpen) {
      setStack({ kind: 'picker' });
      setTemplates([]);
      setTemplateListVersion(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !apiReady) return;
    let cancelled = false;
    (async () => {
      setTemplatesLoading(true);
      try {
        const res = await listFatSpecificationTemplates({
          projectId: projectId as string | number,
          pipeId: pipeId as string | number,
        });
        if (!cancelled) setTemplates(res.templates);
      } catch {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, apiReady, projectId, pipeId, templateListVersion, fatTemplateListVersion]);

  const findTemplateForSpool = useCallback(
    (spoolIndex: number, spoolLabel: string) => findFatSpecificationTemplateForSpool(templates, spoolIndex, spoolLabel),
    [templates],
  );

  const resolveSections = useCallback(
    (spoolIndex: number, spoolLabel: string): CoatingChecklistTemplateSection[] => {
      const draft = draftFromTemplate(findTemplateForSpool(spoolIndex, spoolLabel));
      return draft?.checklistSections?.length ? (draft.checklistSections as CoatingChecklistTemplateSection[]) : createSeedChecklistSections();
    },
    [findTemplateForSpool],
  );

  const buildInitialDraft = useCallback(
    (spoolIndex: number, spoolLabel: string): Partial<CoatingSpoolPersistPayload> => {
      const draft = draftFromTemplate(findTemplateForSpool(spoolIndex, spoolLabel));
      return {
        checklistSections: draft?.checklistSections?.length
          ? (draft.checklistSections as CoatingChecklistTemplateSection[])
          : createSeedChecklistSections(),
        checklistValues:
          draft?.checklistValues && typeof draft.checklistValues === 'object' ? { ...draft.checklistValues } : {},
      };
    },
    [findTemplateForSpool],
  );

  const persistTemplate = useCallback(
    async (spoolIndex: number, spoolLabel: string, payload: CoatingSpoolPersistPayload) => {
      if (!apiReady) {
        Notification.error('FAT checklist', 'Project and pipe context are required to save.');
        return null;
      }
      const existing = findTemplateForSpool(spoolIndex, spoolLabel);
      try {
        const saved = await saveFatSpecificationTemplate({
          ...(existing?.id != null ? { id: existing.id } : {}),
          projectId: Number(projectId),
          pipeId: Number(pipeId),
          spoolIndex,
          category: FAT_CHECKLIST_SPEC_CATEGORY,
          reference: existing?.reference ?? spoolLabel ?? null,
          payload,
        });
        if (!saved) {
          Notification.error('FAT checklist', 'Save failed. Please try again.');
          return null;
        }
        setTemplates((prev) => {
          const map = new Map(prev.map((template) => [template.id, template]));
          map.set(saved.id, saved);
          return Array.from(map.values());
        });
        setTemplateListVersion((v) => v + 1);
        return saved;
      } catch {
        Notification.error('FAT checklist', 'Save failed. Please try again.');
        return null;
      }
    },
    [apiReady, projectId, pipeId, findTemplateForSpool],
  );

  const handleAddCategory = useCallback(
    async (spoolIndex: number, spoolLabel: string, category: string) => {
      const draft = buildInitialDraft(spoolIndex, spoolLabel);
      const newSection: CoatingChecklistTemplateSection = {
        id: newCoatingChecklistId('sec'),
        category,
        items: [{ id: newCoatingChecklistId('chk'), description: '', criteria: 'Accept/Reject/NA', value: 'N/A' }],
      };
      const payload: CoatingSpoolPersistPayload = {
        checklistSections: [...(draft.checklistSections ?? []), newSection],
        checklistValues: draft.checklistValues ?? {},
      };
      const saved = await persistTemplate(spoolIndex, spoolLabel, payload);
      if (!saved) return undefined;
      Notification.success('FAT checklist', `Added category "${category}".`);
      return newSection.id;
    },
    [buildInitialDraft, persistTemplate],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="m-0 h-screen w-screen max-w-none gap-0 rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="shrink-0 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={onClose} aria-label="Close FAT">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-800">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-left text-lg font-bold text-gray-900">
                    {SPEC_TYPE_LABELS.FAT} — checklist
                  </DialogTitle>
                  {project?.project?.projectName ? (
                    <p className="mt-0.5 truncate text-sm text-gray-600">Project: {project.project.projectName}</p>
                  ) : null}
                  {uploadedAt ? <p className="mt-0.5 text-xs text-gray-500">Uploaded {uploadedAt}</p> : null}
                </div>
              </div>
            </div>
          </DialogHeader>

          {stack.kind === 'edit' ? (
            <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50/90 px-4 py-2.5 sm:px-6">
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={() => setStack({ kind: 'picker' })}>
                <ArrowLeft className="h-4 w-4 text-slate-600" />
              </Button>
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold text-slate-900">Edit checklist category</p>
                <p className="m-0 truncate text-xs text-slate-600">{stack.spoolLabel} · {stack.sectionLabel}</p>
              </div>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto max-w-full space-y-6 px-4 py-4 sm:px-6 sm:py-6">
              {stack.kind === 'picker' ? (
                <>
                  {!apiReady ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Project and pipe context are required to load FAT checklists.
                    </div>
                  ) : templatesLoading ? (
                    <p className="m-0 text-sm text-slate-500">Loading FAT checklists…</p>
                  ) : null}

                  {showChecklist && nSpools > 0 ? (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      {norm.map((row, i) => {
                        const spoolIndex = i + 1;
                        const label = row.spoolName?.trim() || defaultSpoolDisplayName(i);
                        const template = findTemplateForSpool(spoolIndex, label);
                        const draft = draftFromTemplate(template);
                        const displaySections = resolveSections(spoolIndex, label);
                        const hasData = Boolean(draft?.checklistSections?.length || Object.keys(draft?.checklistValues || {}).length);
                        return (
                          <FatSpoolPickerCard
                            key={spoolIndex}
                            label={label}
                            displaySections={displaySections}
                            hasData={hasData}
                            canEdit={canEdit}
                            onOpenSection={(sectionId, sectionLabel) =>
                              setStack({ kind: 'edit', spoolIndex, spoolLabel: label, sectionId, sectionLabel })
                            }
                            onAddCategory={(category) => handleAddCategory(spoolIndex, label, category)}
                          />
                        );
                      })}
                    </div>
                  ) : showChecklist && nSpools <= 0 ? (
                    <div className="pt-2 text-sm text-amber-800">
                      <p className="m-0">
                        Set <span className="font-medium">number of spools</span> in Setup (Fabrication → Spools) to add FAT checklists per spool.
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="pb-8">
                  <CoatingSpoolChecklistEditor
                    resetKey={`${specification.id}-fat-sp-${stack.spoolIndex}-sec-${stack.sectionId}-v${templateListVersion}`}
                    initialDraft={buildInitialDraft(stack.spoolIndex, stack.spoolLabel)}
                    singleSectionId={stack.sectionId}
                    canEdit={canEdit}
                    saveLabel="Save FAT checklist"
                    saveButtonClassName="bg-hero-red hover:bg-hero-red-60"
                    onSave={async (payload) => {
                      const saved = await persistTemplate(stack.spoolIndex, stack.spoolLabel, payload);
                      if (saved) Notification.success('FAT checklist', `Saved checklist for ${stack.spoolLabel}.`);
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
